import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { SearchBox } from "../../../components/Search";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { getGenerationEltrixBackdownMetricsSourcePlant,getReGenerationMetricsSourceArea, getIsgsMetricsSourceState} from "../Utility/RestApi";
import { RAJ_UNITS } from "../const_raj";
import { titleCase } from "../../../extensions/extension";
import { stringToConsistentColor } from "../../../extensions/string";
import { lightenColor } from "../../../components/recharts/ReMixCharts";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const GENERATION_TYPE = ["Interstate", "Intrastate"];

interface RajasthanSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedSource: string[];
  onSourceChange: (items: string[]) => void;
  selectedGenerationType: string[];
  onGenerationTypeChange: (items: string[]) => void;
  selectedState: string[];
  onStateChange: (items: string[]) => void;
  states: string[];
  uniqueSource: string[];
  generationStatus: string[];
  setGenerationStatus: (items: string[]) => void;
  onLoad: any[];
  offLoad: any[];
  selectedBlock: any[];
  setSelectedBlock: any;
  selectedStation: any[];
  setSelectedStation: any;
  uniqueStations: string[];
}

export function RajasthanSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedSource,
  onSourceChange,
  selectedGenerationType,
  onGenerationTypeChange,
  selectedState,
  onStateChange,
  states,
  uniqueSource,
  generationStatus,
  setGenerationStatus,
  onLoad,
  offLoad,
  selectedBlock,
  setSelectedBlock,
  selectedStation,
  setSelectedStation,
  uniqueStations,
}: RajasthanSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedGroupBy, setSelectedGroupBy] = useState("Plant");
  const [loading, setLoading] = useState(true);

  // Helper to extract base plant name (strips area suffix)
  const getPlantBaseName = useCallback((fullName: string) => {
    return fullName.replace(" Intrastate", "").replace(" Interstate", "").trim();
  }, []);

  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        console.error("Invalid date range", { dates, activeTab });
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      let allData: any[] = [];
      const plantNamesToSend = selectedStation.map((s: any) =>
        getPlantBaseName(s?.plant_name || s?.value || s)
      );

      const showGeneration =
        generationStatus.length === 0 ||
        generationStatus.some((v) => v.toLowerCase().includes("generation"));
      const reAreasToSend: string[] = [];

      if (showGeneration) {
        if (
          selectedGenerationType.some(
            (type) => type.toLowerCase() === "intrastate"
          )
        ) {
          reAreasToSend.push("IntraState");
        }
        if (
          selectedGenerationType.some(
            (type) => type.toLowerCase() === "interstate"
          )
        ) {
          reAreasToSend.push("InterState");
        }
      }

      const isgsUtilities = selectedStation.map((s: any) =>
        getPlantBaseName(s?.plant_name || s?.value || s)
      );

      const [res, reRes, isgsRes] = await Promise.all([
        (selectedGenerationType.some((type) =>
          ["intrastate", "interstate"].includes(type.toLowerCase())
        ) || selectedStation.length > 0)
          ? getGenerationEltrixBackdownMetricsSourcePlant(
              startDateStr,
              endDateStr,
              selectedSource.length > 0 ? selectedSource : undefined,
              plantNamesToSend.length > 0 ? plantNamesToSend : undefined
            )
          : Promise.resolve(null),
        reAreasToSend.length > 0
          ? getReGenerationMetricsSourceArea(
              startDateStr,
              endDateStr,
              selectedSource.length > 0 ? selectedSource : undefined,
              reAreasToSend
            )
          : Promise.resolve(null),
        (selectedGenerationType.some(
          (type) => type.toLowerCase() === "interstate"
        ) ||
          selectedStation.length > 0 ||
          selectedState.length > 0)
          ? getIsgsMetricsSourceState(
              startDateStr,
              endDateStr,
              selectedSource.length > 0 ? selectedSource : undefined,
              selectedState.length > 0 ? selectedState : undefined,
              isgsUtilities.length > 0 ? isgsUtilities : undefined
            )
          : Promise.resolve(null),
      ]);

      if (res && Array.isArray(res)) {
        const flattenedData = res.flatMap((sourceItem) => {
          const results = [];

          // Add source-level aggregate
          results.push({
            slot: sourceItem.slot,
            source: titleCase(sourceItem.generation_source),
            station: titleCase(sourceItem.generation_source),
            plant_name: "Aggregate",
            isSource: true,
            isRE: false,
            state: "Rajasthan",
            // ONBARDS (Generation)
            min_generation: sourceItem.source_min_onbards,
            max_generation: sourceItem.source_max_onbards,
            avg_generation: sourceItem.source_avg_onbards,
            mode_generation: sourceItem.source_mode_onbards || 0,
            min_generation_date: sourceItem.source_date_of_min_onbards || "",
            max_generation_date: sourceItem.source_date_of_max_onbards || "",
            // ONBARDC (Capacity)
            min_capacity: sourceItem.source_min_onbardc,
            max_capacity: sourceItem.source_max_onbardc,
            avg_capacity: sourceItem.source_avg_onbardc,
            mode_capacity: sourceItem.source_mode_onbardc || 0,
            min_capacity_date: sourceItem.source_date_of_min_onbardc || "",
            max_capacity_date: sourceItem.source_date_of_max_onbardc || "",
            // Backdown
            min_backdown: sourceItem.source_min_backdown,
            max_backdown: sourceItem.source_max_backdown,
            avg_backdown: sourceItem.source_avg_backdown,
            mode_backdown: sourceItem.source_mode_backdown || 0,
            min_backdown_date: sourceItem.source_date_of_min_backdown || "",
            max_backdown_date: sourceItem.source_date_of_max_backdown || "",
            isPlantLevel: false,
          });

          // Add plant-level data
          if (sourceItem.plants && Array.isArray(sourceItem.plants)) {
            sourceItem.plants.forEach((plant: any) => {
              results.push({
                slot: plant.slot,
                source: titleCase(sourceItem.generation_source),
                station: `${plant.plant_name} (${titleCase(
                  sourceItem.generation_source
                )})`,
                plant_name: plant.plant_name,
                isSource: false,
                isRE: false,
                state: "Rajasthan",
                // ONBARDS (Generation)
                min_generation: plant.min_onbards,
                max_generation: plant.max_onbards,
                avg_generation: plant.avg_onbards,
                mode_generation: plant.mode_onbards,
                min_generation_date: plant.date_of_min_onbards,
                max_generation_date: plant.date_of_max_onbards,
                // ONBARDC (Capacity)
                min_capacity: plant.min_onbardc,
                max_capacity: plant.max_onbardc,
                avg_capacity: plant.avg_onbardc,
                mode_capacity: plant.mode_onbardc,
                min_capacity_date: plant.date_of_min_onbardc,
                max_capacity_date: plant.date_of_max_onbardc,
                // Backdown
                min_backdown: plant.min_backdown,
                max_backdown: plant.max_backdown,
                avg_backdown: plant.avg_backdown,
                mode_backdown: plant.mode_backdown,
                min_backdown_date: plant.date_of_min_backdown,
                max_backdown_date: plant.date_of_max_backdown,
                isPlantLevel: true,
              });
            });
          }
          return results;
        });
        allData = [...allData, ...flattenedData];
      }

        if (reRes && Array.isArray(reRes)) {
          const flattenedReData = reRes.flatMap((item: any) => {
            const results = [];
            const aggSector = reAreasToSend.includes("IntraState")
              ? "Intrastate"
              : "Interstate";

            // Add source-level aggregate (preserve region/state if provided)
            results.push({
              slot: item.slot,
              source: titleCase(item.energy_type),
              sector: aggSector,
              station: titleCase(item.energy_type),
              plant_name: "Aggregate",
              isSource: true,
              isRE: true,
              state: item.state || item.region || "Rajasthan",
              // Generation
              min_generation: item.source_min_value,
              max_generation: item.source_max_value,
              avg_generation: item.source_avg_value,
              mode_generation: item.source_mode_value || 0,
              min_generation_date: "",
              max_generation_date: "",
              // Capacity (Set to 0)
              min_capacity: 0,
              max_capacity: 0,
              avg_capacity: 0,
              mode_capacity: 0,
              min_capacity_date: "",
              max_capacity_date: "",
              // Backdown (Set to 0)
              min_backdown: 0,
              max_backdown: 0,
              avg_backdown: 0,
              mode_backdown: 0,
              min_backdown_date: "",
              max_backdown_date: "",
              isPlantLevel: false,
            });

            // Add area-level data
            if (item.areas && Array.isArray(item.areas)) {
              item.areas.forEach((areaData: any) => {
                const areaName = areaData.area;
                const sectorName =
                  areaName === "IntraState"
                    ? "Intrastate"
                    : areaName === "InterState"
                    ? "Interstate"
                    : areaName;

                // try to extract a specific state from areaData if present
                const areaState =
                  areaData.state ||
                  areaData.state_name ||
                  areaData.stateCode ||
                  areaData.state_code ||
                  (areaName &&
                  areaName !== "IntraState" &&
                  areaName !== "InterState"
                    ? areaName
                    : undefined);

                const stationLabel = areaState
                  ? `${titleCase(item.energy_type)} (${areaState})`
                  : `${areaName} (${titleCase(item.energy_type)})`;
                const plantName = areaState || areaName;

                results.push({
                  slot: areaData.slot,
                  source: titleCase(item.energy_type),
                  sector: sectorName,
                  station: stationLabel,
                  plant_name: plantName,
                  isSource: false,
                  isRE: true,
                  state: areaState || item.state || "Rajasthan",
                  // Generation
                  min_generation: areaData.min_value,
                  max_generation: areaData.max_value,
                  avg_generation: areaData.avg_value,
                  mode_generation: areaData.mode_value,
                  min_generation_date: "",
                  max_generation_date: "",
                  // Capacity
                  min_capacity: 0,
                  max_capacity: 0,
                  avg_capacity: 0,
                  mode_capacity: 0,
                  min_capacity_date: "",
                  max_capacity_date: "",
                  // Backdown
                  min_backdown: 0,
                  max_backdown: 0,
                  avg_backdown: 0,
                  mode_backdown: 0,
                  min_backdown_date: "",
                  max_backdown_date: "",
                  isPlantLevel: false,
                });
              });
            }
            return results;
          });
        allData = [...allData, ...flattenedReData];
        }

      if (isgsRes && Array.isArray(isgsRes)) {
        const flattenedIsgs = isgsRes.flatMap((item: any) => {
          const isRE = ["hydro", "solar", "wind", "biomass"].includes(
            item.generation_source.toLowerCase()
          );
          const results = [];

          // Add source-level aggregate
          results.push({
            slot: item.slot,
            source: titleCase(item.generation_source),
            sector: "Interstate",
            station: titleCase(item.generation_source),
            plant_name: "Aggregate",
            isSource: true,
            isRE: isRE,
            state: "Rajasthan",
            // Generation
            min_generation: item.source_min_value,
            max_generation: item.source_max_value,
            avg_generation: item.source_avg_value,
            mode_generation: item.source_mode_value || 0,
            min_generation_date: "",
            max_generation_date: "",
            // Capacity (set to 0)
            min_capacity: 0,
            max_capacity: 0,
            avg_capacity: 0,
            mode_capacity: 0,
            min_capacity_date: "",
            max_capacity_date: "",
            // Backdown (set to 0)
            min_backdown: 0,
            max_backdown: 0,
            avg_backdown: 0,
            mode_backdown: 0,
            min_backdown_date: "",
            max_backdown_date: "",
            isPlantLevel: false,
          });

          if (item.states && Array.isArray(item.states)) {
            item.states.forEach((stateItem: any) => {
              // Add State aggregate
              results.push({
                slot: stateItem.slot,
                source: titleCase(item.generation_source),
                sector: "Interstate",
                station: `${stateItem.state_name} (${titleCase(
                  item.generation_source
                )})`,
                plant_name: stateItem.state_name,
                isSource: false,
                isRE: isRE,
                state: stateItem.state_name,
                // Generation
                min_generation: stateItem.state_min_value,
                max_generation: stateItem.state_max_value,
                avg_generation: stateItem.state_avg_value,
                mode_generation: stateItem.state_mode_value || 0,
                min_generation_date: "",
                max_generation_date: "",
                // Capacity
                min_capacity: 0,
                max_capacity: 0,
                avg_capacity: 0,
                mode_capacity: 0,
                min_capacity_date: "",
                max_capacity_date: "",
                // Backdown
                min_backdown: 0,
                max_backdown: 0,
                avg_backdown: 0,
                mode_backdown: 0,
                min_backdown_date: "",
                max_backdown_date: "",
                isPlantLevel: false,
              });

              // Add Utility data
              if (stateItem.utilities && Array.isArray(stateItem.utilities)) {
                stateItem.utilities.forEach((util: any) => {
                  results.push({
                    slot: util.slot,
                    source: titleCase(item.generation_source),
                    sector: "Interstate",
                    station: `${util.utility_name} (${stateItem.state_name})`,
                    plant_name: util.utility_name,
                    isSource: false,
                    isRE: isRE,
                    state: stateItem.state_name,
                    // Generation
                    min_generation: util.min_value,
                    max_generation: util.max_value,
                    avg_generation: util.avg_value,
                    mode_generation: util.mode_value || 0,
                    min_generation_date: "",
                    max_generation_date: "",
                    // Capacity
                    min_capacity: 0,
                    max_capacity: 0,
                    avg_capacity: 0,
                    mode_capacity: 0,
                    min_capacity_date: "",
                    max_capacity_date: "",
                    // Backdown
                    min_backdown: 0,
                    max_backdown: 0,
                    avg_backdown: 0,
                    mode_backdown: 0,
                    min_backdown_date: "",
                    max_backdown_date: "",
                    isPlantLevel: true,
                  });
                });
              }
            });
          }
          return results;
        });
        allData = [...allData, ...flattenedIsgs];
      }

      setSummaryData(allData);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedGenerationType, selectedStation, generationStatus, selectedState, selectedSource, getPlantBaseName]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    let filteredData = summaryData;

    // Apply source filter
    if (selectedSource.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedSource.includes(item.source)
      );
      console.log("After source filter:", filteredData.length);
    }

    // Apply generation type filter
    if (selectedGenerationType.length > 0) {
      filteredData = filteredData.filter((item) => {
        const matchesSector = selectedGenerationType.includes(item.sector);
        const matchesStation = selectedStation.some(
          (s: any) =>
            (s?.plant_name || getPlantBaseName(s?.value || s)) ===
            item.plant_name
        );
        return matchesSector || matchesStation;
      });
    }

    // Apply state filter (case-insensitive, check common state fields)
    if (selectedState.length > 0) {
      const selectedLower = selectedState.map((s) =>
        (s || "").toString().trim().toLowerCase()
      );
      filteredData = filteredData.filter((item) => {
        const st = item.state || item.state_name || item.region || "";
        return selectedLower.includes(
          (st || "").toString().trim().toLowerCase()
        );
      });
    }

    // Apply block filter
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((b) => b.value).includes(item.slot)
      );
    }

    // Sort data for consistent display
    filteredData = [...filteredData].sort((a, b) => {
      // First sort by source
      if (a.source !== b.source) {
        return a.source.localeCompare(b.source);
      }
      // Then by slot
      if (a.slot !== b.slot) {
        return a.slot - b.slot;
      }
      // Then by plant name (source aggregates first, then plants alphabetically)
      if (a.isSource && !b.isSource) return -1;
      if (!a.isSource && b.isSource) return 1;
      return (a.plant_name || "").localeCompare(b.plant_name || "");
    });

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    // Determine what to show based on Group By and Station selection
    const showPlantLevel =
      selectedGroupBy === "Plant" || selectedStation.length > 0 || selectedState.length > 0;
    const selectedStateLower = selectedState.map((s) =>
      (s || "").toString().trim().toLowerCase()
    );
    let chartFilteredData = filteredData.filter((item) => {
      if (showPlantLevel) {
        // Show only plant-level data (not source aggregates)
        if (item.isSource) return false;

        // If specific stations are selected, filter by them
        if (selectedStation.length > 0) {
          const selectedPlantNames = selectedStation.map(
            (s: any) => s?.plant_name || getPlantBaseName(s?.value || s)
          );
          return selectedPlantNames.includes(item.plant_name);
        }

        // Show all plants (isPlantLevel: true) OR RE data (which might be area aggregates)
        return item.isPlantLevel || item.isRE;
      } else {
        // Show only source-level aggregates by default
        // If a state is selected, also include state-level aggregates (for ISGS/interstate data)
        if (selectedState.length > 0) {
          const itemState = (item.state || item.state_name || item.region || "")
            .toString()
            .trim()
            .toLowerCase();
          const isStateAggregate =
            !item.isSource &&
            itemState &&
            selectedStateLower.includes(itemState);
          return item.isSource || isStateAggregate;
        }
        return item.isSource;
      }
    });

    const convertedData = chartFilteredData.map((item) => ({
      ...item,
      min_generation: (item.min_generation || 0) * conversionFactor,
      max_generation: (item.max_generation || 0) * conversionFactor,
      avg_generation: (item.avg_generation || 0) * conversionFactor,
      mode_generation: (item.mode_generation || 0) * conversionFactor,
      min_capacity: (item.min_capacity || 0) * conversionFactor,
      max_capacity: (item.max_capacity || 0) * conversionFactor,
      avg_capacity: (item.avg_capacity || 0) * conversionFactor,
      mode_capacity: (item.mode_capacity || 0) * conversionFactor,
      min_backdown: (item.min_backdown || 0) * conversionFactor,
      max_backdown: (item.max_backdown || 0) * conversionFactor,
      avg_backdown: (item.avg_backdown || 0) * conversionFactor,
      mode_backdown: (item.mode_backdown || 0) * conversionFactor,
    }));

    setChartData(convertedData);
    setTableData(convertedData);
  }, [
    summaryData,
    selectedUnit,
    selectedSource,
    selectedGenerationType,
    selectedState,
    selectedBlock,
    selectedStation,
    selectedGroupBy,
    getPlantBaseName,
  ]);

  const legends = useMemo(() => {
    const showGeneration = generationStatus.length === 0 || generationStatus.some(v => v.toLowerCase().includes("generation"));
    const showCapacity = generationStatus.length === 0 || generationStatus.some(v => v.toLowerCase().includes("capacity") || v.toLowerCase().includes("declared"));
    const showBackdown = generationStatus.length === 0 || generationStatus.some(v => v.toLowerCase().includes("backdown"));

    const uniqueCategories = Array.from(new Set(chartData.map((item) => item.station)));
    const dynamicLegends: any[] = [];

    uniqueCategories.forEach((catName) => {
      // Check if this category is RE based on data lookup
      const representativeItem = chartData.find(item => item.station === catName);
      const isRE = representativeItem?.isRE === true;

      // Generation
      if (showGeneration) {
        const color = stringToConsistentColor(`${catName}_generation`);
        dynamicLegends.push(
          { 
            dataKey: `${catName}_min_generation`, 
            name: isRE ? `${catName} - Min` : `${catName} - Min Generation`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "min_gen" : undefined, 
            dateKey: `${catName}_min_generation_date`,
          },
          { 
            dataKey: `${catName}_max_generation`, 
            name: isRE ? `${catName} - Max` : `${catName} - Max Generation`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "max_gen" : undefined, 
            dateKey: `${catName}_max_generation_date`,
          },
          { 
            dataKey: `${catName}_avg_generation`, 
            name: isRE ? `${catName} - Avg` : `${catName} - Avg Generation`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "avg_gen" : undefined,
          },
          { 
            dataKey: `${catName}_mode_generation`, 
            name: isRE ? `${catName} - Mode` : `${catName} - Mode Generation`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "mode_gen" : undefined,
           
          }
        );
      }
      // Capacity (Hide for RE)
      if (showCapacity && !isRE) {
        const color = stringToConsistentColor(`${catName}_capacity`);
        dynamicLegends.push(
          { 
            dataKey: `${catName}_min_capacity`, 
            name: `${catName} - Min Capacity`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "min_cap" : undefined, 
            dateKey: `${catName}_min_capacity_date`,
          
          },
          { 
            dataKey: `${catName}_max_capacity`, 
            name: `${catName} - Max Capacity`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "max_cap" : undefined, 
            dateKey: `${catName}_max_capacity_date`,
          
          },
          { 
            dataKey: `${catName}_avg_capacity`, 
            name: `${catName} - Avg Capacity`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "avg_cap" : undefined,
            
            
          },
          { 
            dataKey: `${catName}_mode_capacity`, 
            name: `${catName} - Mode Capacity`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "mode_cap" : undefined,
            
          }
        );
      }
      // Backdown (Hide for RE)
      if (showBackdown && !isRE) {
        const color = stringToConsistentColor(`${catName}_backdown`);
        dynamicLegends.push(
          { 
            dataKey: `${catName}_min_backdown`, 
            name: `${catName} - Min Backdown`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "min_back" : undefined, 
            dateKey: `${catName}_min_backdown_date`,
          },
          { 
            dataKey: `${catName}_max_backdown`, 
            name: `${catName} - Max Backdown`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "max_back" : undefined, 
            dateKey: `${catName}_max_backdown_date`,
          },
          { 
            dataKey: `${catName}_avg_backdown`, 
            name: `${catName} - Avg Backdown`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "avg_back" : undefined,
          },
          { 
            dataKey: `${catName}_mode_backdown`, 
            name: `${catName} - Mode Backdown`, 
            type: selectedChartType.value, 
            stackId: selectedChartType.stackId !== undefined ? "mode_back" : undefined,
            
          }
        );
      }
    });
    return dynamicLegends;
  }, [chartData, selectedChartType, generationStatus]);

  const transformedChartData = useMemo(() => {
    const slotMap: { [key: number]: any } = {};
    
    chartData.forEach((item) => {
      const slotKey = item.slot;
      const catName = item.station;
      
      if (!slotMap[slotKey]) {
        slotMap[slotKey] = {
          slot: item.slot,
          slot_number: item.slot,
        };
      }
      
      // Pivot all metrics
      slotMap[slotKey][`${catName}_min_generation`] = item.min_generation;
      slotMap[slotKey][`${catName}_max_generation`] = item.max_generation;
      slotMap[slotKey][`${catName}_avg_generation`] = item.avg_generation;
      slotMap[slotKey][`${catName}_mode_generation`] = item.mode_generation;
      slotMap[slotKey][`${catName}_min_generation_date`] = item.min_generation_date;
      slotMap[slotKey][`${catName}_max_generation_date`] = item.max_generation_date;

      slotMap[slotKey][`${catName}_min_capacity`] = item.min_capacity;
      slotMap[slotKey][`${catName}_max_capacity`] = item.max_capacity;
      slotMap[slotKey][`${catName}_avg_capacity`] = item.avg_capacity;
      slotMap[slotKey][`${catName}_mode_capacity`] = item.mode_capacity;
      slotMap[slotKey][`${catName}_min_capacity_date`] = item.min_capacity_date;
      slotMap[slotKey][`${catName}_max_capacity_date`] = item.max_capacity_date;

      slotMap[slotKey][`${catName}_min_backdown`] = item.min_backdown;
      slotMap[slotKey][`${catName}_max_backdown`] = item.max_backdown;
      slotMap[slotKey][`${catName}_avg_backdown`] = item.avg_backdown;
      slotMap[slotKey][`${catName}_mode_backdown`] = item.mode_backdown;
      slotMap[slotKey][`${catName}_max_backdown`] = item.max_backdown;
      slotMap[slotKey][`${catName}_avg_backdown`] = item.avg_backdown;
      slotMap[slotKey][`${catName}_min_backdown_date`] = item.min_backdown_date;
      slotMap[slotKey][`${catName}_max_backdown_date`] = item.max_backdown_date;
    });
    
    const transformed = Object.values(slotMap).sort((a: any, b: any) => a.slot - b.slot);
    return transformed;
  }, [chartData]);

  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      const legendItem = legends.find((l) => l.name === seriesName);
      if (legendItem && legendItem.dateKey && dataPoint?.[legendItem.dateKey]) {
        return `${seriesName} (${dataPoint[legendItem.dateKey]})`;
      }
      return seriesName;
    },
    [legends],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: transformedChartData,
    tableData: tableData,
    unit: selectedUnit,
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Generation Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={RAJ_UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          <FilterMenu
            title="Variable"
            items={onLoad.concat(offLoad).map((item) => item.label)}
            selectedItems={generationStatus}
            setSelectedItems={setGenerationStatus}
          />

          <FilterMenu
            title="Source"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={onSourceChange}
          />

          <FilterMenu
            title="Area"
            items={GENERATION_TYPE}
            setSelectedItems={onGenerationTypeChange}
            selectedItems={selectedGenerationType}
          />

          <FilterMenu
            title="State"
            items={states}
            selectedItems={selectedState}
            setSelectedItems={onStateChange}
          />



          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Generation Summary" subtitle="Fetching aggregated generation summary data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">
              Generation Summary ({selectedUnit})
            </h2>
            <div className="flex gap-x-2 w-full justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />

              <SearchBox
                options={uniqueStations
                  .filter((item) => !item.includes("RE IntraState"))
                  .map((item) => {
                    return { 
                      value: item, 
                      label: item,
                      plant_name: getPlantBaseName(item)
                    };
                  })}
                isMany={true}
                placeholder="Search Station"
                selectedOptions={selectedStation}
                onChange={setSelectedStation}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV({
                      ...tablePar,
                    });
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
                {transformedChartData.length === 0 && !loading && (
            <div
              style={{ padding: "40px", textAlign: "center", color: "#666" }}
            >
              No data available...
            </div>
          )}
          {dataTab.label === "Chart" && (
            <ReMixChart
              key={`summary-${selectedUnit}-${selectedChartType.value}-${selectedGroupBy}-${transformedChartData.length}`}
              data={transformedChartData}
              showBrush={undefined}
              isTimeSlot={true}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={legends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey="slot"
              secondXDataKey={undefined}
              resolveDisplayName={resolveTooltipName}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  Intrastate generation : ELTRIX Backdown Reports, Intrastate RE
                  generation : NREB Grid Curves Interstate, generation : Net
                  schedule report breakup ISGS (WBES), Interstate RE generation:
                  Net schedule report breakup OA REMC (WBES)
                </p>
              }
              text="1. Generation = Scheduled Generation for Intrastate Conventional Generating Stations.
2. Capacity (Declared): Declared capacity for Intrastate Conventional Generating Stations.
3. RE Power = Intrastate RE (Solar + Wind + Biomass) Generation.
4. MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}